import { type ScopedPermissions } from '@air/api';

export const canSeePrivateWorkspaceDetails = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canView;

export const canSeeWorkspaceMembers = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canView;

export const canAddWorkspaceMembers = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageMembers;

export const canRemoveWorkspaceMembers = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageMembers;

export const canChangeWorkspaceMemberRole = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageMembers;

export const canChangeWorkspaceDetails = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canEdit;

export const canSeeWorkspaceIntegrationDetails = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canView;

export const canChangeWorkspaceIntegrationDetails = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canEdit;

export const canSeeCustomFields = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.customField.canView;

export const canCreateCustomFields = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.customField.canCreate;

export const canEditCustomFields = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.customField.canEdit;

export const canDeleteCustomFields = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.customField.canDelete;

export const canViewSavedFilters = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canView;

export const canCreateSavedFilters = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageSavedFilters;

export const canEditSavedFilters = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageSavedFilters;

export const canDeleteSavedFilters = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageSavedFilters;

export const canSeeWorkspaceSmartTags = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.tag.canView;

export const canSeeWorkspaceTags = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.tag.canView;

export const canCreateTag = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.tag.canCreate;

export const canUpdateTagName = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.tag.canEdit;

export const canDeleteTag = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.tag.canDelete;

export const canViewAddOns = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.workspace.canView;
export const canEditAddOns = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.addOn.canEdit;

export const canViewSubscription = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canView;

export const canManageSubscription = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageSubscription;

export const canCreateWorkspaceInviteLink = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageMembers;

export const canDeleteWorkspaceInviteLink = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageMembers;

export const canChangeWorkspaceApprovedDomains = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageMembers && !!workspaceContext?.workspace.canEdit;

export const canViewPeople = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.person.canView;
export const canEditPerson = (workspaceContext: ScopedPermissions | undefined) => !!workspaceContext?.person.canEdit;
export const canDeletePerson = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.person.canDelete;

export const canDeleteCdnLink = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.asset.canDeleteCdnLink;

// Collections
export const canCreateCollection = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.collection.canCreate;
export const canEditCollection = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.collection.canEdit;
export const canDeleteCollection = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.collection.canDelete;
export const canViewAnalyticsConnections = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.integration.canViewAnalytics;

export const canCreateAnalyticsConnections = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.integration.canCreateAnalytics;

export const canEditAnalyticsConnections = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.integration.canEditAnalytics;

export const canDeleteAnalyticsConnections = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.integration.canDeleteAnalytics;

export const canViewCreativePerformance = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.integration.canViewAnalytics;

export const canViewContextSources = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canView;

export const canEditContextSources = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageCanvases;

export const canLinkAdsToAssets = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.asset.canEdit && !!workspaceContext?.asset.canManageMetricSources;

export const canImportAssetsFromAd = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.asset.canCreate && !!workspaceContext?.asset.canManageMetricSources;

// Canvases — rule numbers reference backend PR #6829 (rules 190.0–200.0)

/** Rule 198: Create canvas in the workspace (any context) */
export const canCreateCanvasInWorkspace = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.asset.canCreate;

/** Rules 191/193: Delete canvas in workspace. Caller must also check ownership or canManageCanvases. */
export const canDeleteCanvasFromWorkspace = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.asset.canDelete;

/** Rule 200: Edit own canvas in workspace (any context). Caller must also check ownership. */
export const canEditCanvasInWorkspace = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.asset.canEdit;

/** Rules 195/196: View canvas in workspace. Caller must also check ownership or canManageCanvases. */
export const canViewCanvasInWorkspace = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.asset.canView;

/** Rules 190/191/195: Required for non-own canvas actions. Not yet in @air/api types. */
export const canManageCanvasesInWorkspace = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageCanvases;

// Skills
export const canViewSkills = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canViewSkills;

export const canViewAllSkills = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canViewAllSkills;

export const canManageSkills = (workspaceContext: ScopedPermissions | undefined) =>
  !!workspaceContext?.workspace.canManageSkills;
